import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  App,
  Button,
  Calendar,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Drawer,
  Form,
  Input,
  InputNumber,
  Radio,
  Row,
  Segmented,
  Space,
  Tag,
  TimePicker,
  Typography,
} from 'antd';
import { LeftOutlined, PlusOutlined, RightOutlined } from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import { useAuthStore } from '@/store/authStore';
import crmCalendarService, {
  type CrmCalendarEvent,
  type CrmCalendarKind,
  type CrmCalendarLayer,
  type CrmCalendarScope,
} from '@/services/crmCalendar.service';
import { useCompleteCrmFollowUp, useRescheduleCrmFollowUp } from '@/hooks/useCrmActivities';
import CrmRecordPicker, { type CrmRecordLink } from '@/components/crm/activities/CrmRecordPicker';

const { Title, Text } = Typography;

const LAYERS: { value: CrmCalendarLayer; label: string }[] = [
  { value: 'logs', label: 'Daily logs' },
  { value: 'followups', label: 'Follow-ups' },
  { value: 'demos', label: 'Demos' },
  { value: 'personal', label: 'Tasks & reminders' },
];

const KIND_COLOR: Record<CrmCalendarKind, string> = {
  activity: 'blue',
  follow_up: 'orange',
  demo: 'purple',
  task: 'green',
  reminder: 'gold',
};

const KIND_LABEL: Record<CrmCalendarKind, string> = {
  activity: 'Log',
  follow_up: 'Follow-up',
  demo: 'Demo',
  task: 'Task',
  reminder: 'Reminder',
};

const HOURS = Array.from({ length: 11 }, (_, i) => i + 8);

type ViewMode = 'month' | 'week' | 'day' | 'agenda';

function rangeFor(view: ViewMode, cursor: Dayjs): { from: string; to: string } {
  if (view === 'day') {
    const day = cursor.format('YYYY-MM-DD');
    return { from: day, to: day };
  }
  if (view === 'week' || view === 'agenda') {
    const start = cursor.startOf('week');
    return { from: start.format('YYYY-MM-DD'), to: start.add(6, 'day').format('YYYY-MM-DD') };
  }
  const start = cursor.startOf('month').startOf('week');
  const end = cursor.endOf('month').endOf('week');
  return { from: start.format('YYYY-MM-DD'), to: end.format('YYYY-MM-DD') };
}

function eventDay(event: CrmCalendarEvent): string {
  return dayjs(event.start).format('YYYY-MM-DD');
}

function eventHour(event: CrmCalendarEvent): number | null {
  if (event.allDay || event.start.length <= 10) return null;
  return dayjs(event.start).hour();
}

interface EntryForm {
  entry_kind: 'task' | 'reminder';
  title: string;
  notes?: string;
  all_day?: boolean;
  date: Dayjs;
  time?: Dayjs | null;
  end_time?: Dayjs | null;
  remind_minutes_before?: number | null;
  record?: CrmRecordLink | null;
}

const SalesCalendarPage: React.FC = () => {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const qc = useQueryClient();
  const { user } = useAuthStore();
  const [params, setParams] = useSearchParams();
  const canViewTeam = Boolean(user?.is_super_admin || user?.permissions?.includes('crm.activity.view_team'));
  const canWrite = Boolean(
    user?.is_super_admin || user?.permissions?.some((p) => p === 'crm.activity.create' || p === 'crm.activity.edit')
  );

  const [view, setView] = useState<ViewMode>('month');
  const [scope, setScope] = useState<CrmCalendarScope>('mine');
  const [layers, setLayers] = useState<CrmCalendarLayer[]>(LAYERS.map((l) => l.value));
  const [statusFilter, setStatusFilter] = useState<'open' | 'all'>('open');
  const [cursor, setCursor] = useState(() => dayjs());
  const [entryOpen, setEntryOpen] = useState(false);
  const [editing, setEditing] = useState<CrmCalendarEvent | null>(null);
  const [followUp, setFollowUp] = useState<CrmCalendarEvent | null>(null);
  const [entryForm] = Form.useForm<EntryForm>();
  const [followForm] = Form.useForm();
  const skipSelect = useRef(false);

  const range = useMemo(() => rangeFor(view, cursor), [view, cursor]);
  const { data: events = [], isLoading } = useQuery({
    queryKey: ['crm', 'calendar', range.from, range.to, scope, layers.join(',')],
    queryFn: () => crmCalendarService.list({ ...range, scope, layers }),
  });

  const visible = useMemo(
    () =>
      events.filter((e) => {
        const day = eventDay(e);
        if (day < range.from || day > range.to) return false;
        if (statusFilter === 'all') return true;
        const st = String(e.status || '').toLowerCase();
        if (!st) return true;
        return st === 'open' || st === 'planned' || st === 'scheduled';
      }),
    [events, range.from, range.to, statusFilter]
  );

  const byDay = useMemo(() => {
    const map = new Map<string, CrmCalendarEvent[]>();
    for (const event of visible) {
      const day = eventDay(event);
      const list = map.get(day) || [];
      list.push(event);
      map.set(day, list);
    }
    return map;
  }, [visible]);

  const refresh = () => qc.invalidateQueries({ queryKey: ['crm', 'calendar'] });

  const saveEntry = useMutation({
    mutationFn: async (values: EntryForm) => {
      const date = values.date.format('YYYY-MM-DD');
      const start = values.all_day || !values.time ? date : dayjs(`${date}T${values.time.format('HH:mm')}`).toISOString();
      const end =
        !values.all_day && values.end_time ? dayjs(`${date}T${values.end_time.format('HH:mm')}`).toISOString() : null;
      const body = {
        entry_kind: values.entry_kind,
        title: values.title.trim(),
        notes: values.notes?.trim() || null,
        all_day: !!values.all_day,
        starts_at: start,
        ends_at: end,
        remind_minutes_before:
          values.remind_minutes_before == null ? null : Number(values.remind_minutes_before),
        lead_id: values.record?.kind === 'lead' ? values.record.id : null,
        opportunity_id: values.record?.kind === 'opportunity' ? values.record.id : null,
        customer_id: values.record?.kind === 'customer' ? values.record.id : null,
      };
      if (editing && (editing.kind === 'task' || editing.kind === 'reminder')) {
        return crmCalendarService.update(editing.id, body);
      }
      return crmCalendarService.create(body);
    },
    onSuccess: () => {
      message.success(editing ? 'Calendar item updated' : 'Calendar item added');
      setEntryOpen(false);
      setEditing(null);
      refresh();
    },
    onError: (e: Error) => message.error(e.message || 'Could not save'),
  });

  const closeEntry = useMutation({
    mutationFn: async (status: 'Done' | 'Cancelled') => {
      if (!editing) throw new Error('Nothing selected');
      return status === 'Done' ? crmCalendarService.complete(editing.id) : crmCalendarService.cancel(editing.id);
    },
    onSuccess: (_row, status) => {
      message.success(status === 'Done' ? 'Marked done' : 'Cancelled');
      setEntryOpen(false);
      setEditing(null);
      refresh();
    },
    onError: (e: Error) => message.error(e.message || 'Could not update'),
  });

  const completeFollowUp = useCompleteCrmFollowUp();
  const rescheduleFollowUp = useRescheduleCrmFollowUp();

  const openCreate = (slot: Dayjs, allDay = false) => {
    if (!canWrite) return;
    setEditing(null);
    entryForm.setFieldsValue({
      entry_kind: 'task',
      title: '',
      notes: '',
      all_day: allDay,
      date: slot,
      time: allDay ? null : slot,
      end_time: null,
      remind_minutes_before: null,
      record: null,
    });
    setEntryOpen(true);
  };

  const openEvent = (event: CrmCalendarEvent) => {
    if (event.kind === 'follow_up') {
      setFollowUp(event);
      const start = dayjs(event.start);
      followForm.setFieldsValue({
        follow_up_date: start,
        follow_up_time: event.allDay ? null : start,
        completion_note: '',
        reason: '',
      });
      return;
    }
    if (event.kind === 'task' || event.kind === 'reminder') {
      setEditing(event);
      const start = dayjs(event.start);
      const record: CrmRecordLink | null = event.opportunityId
        ? { kind: 'opportunity', id: event.opportunityId }
        : event.leadId
          ? { kind: 'lead', id: event.leadId }
          : event.customerId
            ? { kind: 'customer', id: event.customerId }
            : null;
      entryForm.setFieldsValue({
        entry_kind: event.kind,
        title: event.title,
        notes: event.notes || '',
        all_day: event.allDay,
        date: start,
        time: event.allDay ? null : start,
        end_time: event.end ? dayjs(event.end) : null,
        remind_minutes_before: event.remindMinutesBefore,
        record,
      });
      setEntryOpen(true);
      return;
    }
    navigate(event.link);
  };

  const openedEntry = useRef<string | null>(null);
  useEffect(() => {
    const id = params.get('entry');
    if (!id || openedEntry.current === id) return;
    openedEntry.current = id;
    const found = events.find((e) => e.id === id && (e.kind === 'task' || e.kind === 'reminder'));
    const clear = () => {
      params.delete('entry');
      setParams(params, { replace: true });
    };
    if (found) {
      openEvent(found);
      clear();
      return;
    }
    crmCalendarService
      .get(id)
      .then((event) => {
        openEvent(event);
        clear();
      })
      .catch(() => undefined);
  }, [events, params, setParams]);

  const shift = (dir: number) => {
    const unit = view === 'month' ? 'month' : view === 'day' ? 'day' : 'week';
    setCursor((c) => c.add(dir, unit));
  };

  const logActivity = (slot: Dayjs) => {
    const at = encodeURIComponent(slot.toISOString());
    navigate(`/growth/activities/new?at=${at}&return_to=${encodeURIComponent('/growth/calendar')}`);
  };

  const renderChip = (event: CrmCalendarEvent) => (
    <Tag
      key={`${event.kind}-${event.id}`}
      color={KIND_COLOR[event.kind]}
      style={{ marginBottom: 2, cursor: 'pointer', maxWidth: '100%', whiteSpace: 'normal' }}
      onMouseDown={(e) => {
        e.stopPropagation();
        skipSelect.current = true;
      }}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        openEvent(event);
      }}
    >
      {event.allDay ? '' : `${dayjs(event.start).format('HH:mm')} `}
      {event.title}
    </Tag>
  );

  const days = useMemo(() => {
    if (view === 'day') return [cursor.startOf('day')];
    const start = cursor.startOf('week');
    return Array.from({ length: 7 }, (_, i) => start.add(i, 'day'));
  }, [view, cursor]);

  const entryKind = Form.useWatch('entry_kind', entryForm);
  const entryAllDay = Form.useWatch('all_day', entryForm);

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Space wrap style={{ width: '100%', justifyContent: 'space-between' }}>
          <Title level={3} style={{ margin: 0 }}>
            Sales calendar
          </Title>
          <Space wrap>
            <Button icon={<LeftOutlined />} onClick={() => shift(-1)} />
            <Button onClick={() => setCursor(dayjs())}>Today</Button>
            <Button icon={<RightOutlined />} onClick={() => shift(1)} />
            <Text strong>
              {view === 'day'
                ? cursor.format('DD MMM YYYY')
                : view === 'agenda' || view === 'week'
                  ? `${cursor.startOf('week').format('DD MMM')} – ${cursor.startOf('week').add(6, 'day').format('DD MMM YYYY')}`
                  : cursor.format('MMMM YYYY')}
            </Text>
          </Space>
        </Space>
        <Space wrap>
          <Segmented
            value={view}
            onChange={(v) => setView(v as ViewMode)}
            options={[
              { label: 'Month', value: 'month' },
              { label: 'Week', value: 'week' },
              { label: 'Day', value: 'day' },
              { label: 'Agenda', value: 'agenda' },
            ]}
          />
          {canViewTeam && (
            <Segmented
              value={scope}
              onChange={(v) => setScope(v as CrmCalendarScope)}
              options={[
                { label: 'Mine', value: 'mine' },
                { label: 'Team', value: 'team' },
              ]}
            />
          )}
          <Segmented
            value={statusFilter}
            onChange={(v) => setStatusFilter(v as 'open' | 'all')}
            options={[
              { label: 'Open', value: 'open' },
              { label: 'All statuses', value: 'all' },
            ]}
          />
          <Checkbox.Group
            options={LAYERS}
            value={layers}
            onChange={(v) => setLayers(v as CrmCalendarLayer[])}
          />
          {canWrite && (
            <>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => openCreate(cursor.hour(9).minute(0))}>
                Add task or reminder
              </Button>
              <Button onClick={() => logActivity(cursor.hour(9).minute(0))}>Log activity</Button>
            </>
          )}
        </Space>
        <Text type="secondary">
          Unified CRM calendar: daily logs, follow-ups, demos, and personal tasks. Open an item for customer context,
          complete or reschedule without leaving the calendar. Changes sync to the mobile app.
        </Text>
        <Card loading={isLoading} styles={{ body: { padding: view === 'month' ? 8 : 12 } }}>
          {view === 'month' ? (
            <Calendar
              value={cursor}
              onPanelChange={(d) => setCursor(d)}
              onSelect={(d, info) => {
                if (info.source !== 'date') return;
                setCursor(d);
                if (skipSelect.current) {
                  skipSelect.current = false;
                  return;
                }
                openCreate(d.hour(9).minute(0), true);
              }}
              cellRender={(d, info) => {
                if (info.type !== 'date') return info.originNode;
                const items = byDay.get(d.format('YYYY-MM-DD')) || [];
                if (!items.length) return null;
                return <div>{items.slice(0, 3).map(renderChip)}{items.length > 3 ? <Text type="secondary">+{items.length - 3}</Text> : null}</div>;
              }}
            />
          ) : view === 'agenda' ? (
            <Space direction="vertical" style={{ width: '100%' }} size={8}>
              {visible.length === 0 ? (
                <Text type="secondary">No activities in this week.</Text>
              ) : (
                [...visible]
                  .sort((a, b) => String(a.start).localeCompare(String(b.start)))
                  .map((event) => (
                    <Card
                      key={`${event.kind}-${event.id}`}
                      size="small"
                      hoverable
                      onClick={() => openEvent(event)}
                      styles={{ body: { display: 'flex', gap: 12, alignItems: 'flex-start' } }}
                    >
                      <div style={{ minWidth: 110 }}>
                        <Text strong>{dayjs(event.start).format('ddd D MMM')}</Text>
                        <br />
                        <Text type="secondary">
                          {event.allDay || event.start.length <= 10 ? 'All day' : dayjs(event.start).format('HH:mm')}
                        </Text>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Space wrap size={4}>
                          <Tag color={KIND_COLOR[event.kind]}>{KIND_LABEL[event.kind]}</Tag>
                          {event.status ? <Tag>{event.status}</Tag> : null}
                        </Space>
                        <div>
                          <Text strong>{event.title}</Text>
                        </div>
                        <Text type="secondary">
                          {[event.party, event.ownerName].filter(Boolean).join(' · ')}
                        </Text>
                      </div>
                    </Card>
                  ))
              )}
            </Space>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <Row gutter={8} wrap={false}>
                <Col flex="72px" />
                {days.map((day) => (
                  <Col key={day.format('YYYY-MM-DD')} flex="1 0 140px">
                    <Text strong>{day.format('ddd D')}</Text>
                  </Col>
                ))}
              </Row>
              <Row gutter={8} wrap={false} style={{ marginTop: 8 }}>
                <Col flex="72px">
                  <Text type="secondary">All day</Text>
                </Col>
                {days.map((day) => {
                  const key = day.format('YYYY-MM-DD');
                  const items = (byDay.get(key) || []).filter((e) => eventHour(e) == null);
                  return (
                    <Col
                      key={key}
                      flex="1 0 140px"
                      style={{ minHeight: 48, border: '1px dashed var(--ant-color-border-secondary, #f0f0f0)', padding: 4, cursor: 'pointer' }}
                      onClick={() => openCreate(day.hour(9).minute(0), true)}
                    >
                      {items.map(renderChip)}
                    </Col>
                  );
                })}
              </Row>
              {HOURS.map((hour) => (
                <Row key={hour} gutter={8} wrap={false}>
                  <Col flex="72px">
                    <Text type="secondary">{String(hour).padStart(2, '0')}:00</Text>
                  </Col>
                  {days.map((day) => {
                    const key = day.format('YYYY-MM-DD');
                    const items = (byDay.get(key) || []).filter((e) => eventHour(e) === hour);
                    return (
                      <Col
                        key={`${key}-${hour}`}
                        flex="1 0 140px"
                        style={{ minHeight: 42, borderTop: '1px solid var(--ant-color-border-secondary, #f0f0f0)', padding: 2, cursor: 'pointer' }}
                        onClick={() => openCreate(day.hour(hour).minute(0))}
                      >
                        {items.map(renderChip)}
                      </Col>
                    );
                  })}
                </Row>
              ))}
            </div>
          )}
        </Card>
      </Space>

      <Drawer
        title={editing ? 'Task or reminder' : 'Add task or reminder'}
        open={entryOpen}
        onClose={() => {
          setEntryOpen(false);
          setEditing(null);
        }}
        width={440}
        extra={
          canWrite ? (
            <Button type="primary" loading={saveEntry.isPending} onClick={() => entryForm.submit()}>
              Save
            </Button>
          ) : null
        }
      >
        <Form<EntryForm> form={entryForm} layout="vertical" onFinish={(v) => saveEntry.mutate(v)} disabled={!canWrite}>
          <Form.Item name="entry_kind" label="Kind" rules={[{ required: true }]}>
            <Radio.Group>
              <Radio.Button value="task">Task</Radio.Button>
              <Radio.Button value="reminder">Reminder</Radio.Button>
            </Radio.Group>
          </Form.Item>
          <Form.Item name="title" label="Title" rules={[{ required: true, message: 'Enter a title' }]}>
            <Input maxLength={255} />
          </Form.Item>
          <Form.Item name="all_day" valuePropName="checked">
            <Checkbox>All day</Checkbox>
          </Form.Item>
          <Row gutter={8}>
            <Col span={entryAllDay ? 24 : 12}>
              <Form.Item name="date" label="Date" rules={[{ required: true }]}>
                <DatePicker format="DD-MM-YYYY" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            {!entryAllDay && (
              <Col span={12}>
                <Form.Item name="time" label="Start" rules={[{ required: true, message: 'Pick a time' }]}>
                  <TimePicker format="HH:mm" minuteStep={15} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            )}
          </Row>
          {!entryAllDay && (
            <Form.Item name="end_time" label="End">
              <TimePicker format="HH:mm" minuteStep={15} style={{ width: '100%' }} />
            </Form.Item>
          )}
          <Form.Item
            name="remind_minutes_before"
            label="Remind me before (minutes)"
            tooltip={entryKind === 'reminder' ? 'Leave blank to remind 15 minutes before' : 'Optional'}
          >
            <InputNumber min={0} max={10080} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="record" label="Customer, lead, or opportunity">
            <CrmRecordPicker />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
        {editing && editing.status === 'Open' && canWrite && (
          <Space>
            <Button loading={closeEntry.isPending} onClick={() => closeEntry.mutate('Done')}>
              Mark done
            </Button>
            <Button danger loading={closeEntry.isPending} onClick={() => closeEntry.mutate('Cancelled')}>
              Cancel item
            </Button>
          </Space>
        )}
        {editing?.party && (
          <div style={{ marginTop: 12 }}>
            <Text type="secondary">{editing.party}</Text>
          </div>
        )}
      </Drawer>

      <Drawer title="Follow-up" open={!!followUp} onClose={() => setFollowUp(null)} width={420}>
        {followUp && (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Space wrap>
              <Tag color={KIND_COLOR.follow_up}>{KIND_LABEL.follow_up}</Tag>
              {followUp.status ? <Tag>{followUp.status}</Tag> : null}
            </Space>
            <Text strong>{followUp.title}</Text>
            {followUp.party && <Text type="secondary">{followUp.party}</Text>}
            {followUp.ownerName && <Text type="secondary">Owner: {followUp.ownerName}</Text>}
            <Text type="secondary">
              {followUp.allDay || followUp.start.length <= 10
                ? dayjs(followUp.start).format('DD MMM YYYY')
                : dayjs(followUp.start).format('DD MMM YYYY HH:mm')}
            </Text>
            <Button type="link" style={{ padding: 0 }} onClick={() => navigate(followUp.link)}>
              Open CRM record
            </Button>
            <Form
              form={followForm}
              layout="vertical"
              onFinish={async (v) => {
                await rescheduleFollowUp.mutateAsync({
                  id: followUp.id,
                  follow_up_date: (v.follow_up_date as Dayjs).format('YYYY-MM-DD'),
                  follow_up_time: v.follow_up_time ? (v.follow_up_time as Dayjs).format('HH:mm:ss') : null,
                  reason: v.reason || undefined,
                });
                setFollowUp(null);
                refresh();
              }}
            >
              <Form.Item name="follow_up_date" label="New date" rules={[{ required: true }]}>
                <DatePicker format="DD-MM-YYYY" style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="follow_up_time" label="Time">
                <TimePicker format="HH:mm" minuteStep={15} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="reason" label="Reason">
                <Input.TextArea rows={2} />
              </Form.Item>
              <Button htmlType="submit" loading={rescheduleFollowUp.isPending}>
                Reschedule
              </Button>
            </Form>
            <Form
              layout="vertical"
              onFinish={async (v) => {
                await completeFollowUp.mutateAsync({
                  id: followUp.id,
                  body: { status: 'Done', completion_note: v.completion_note || undefined },
                });
                setFollowUp(null);
                refresh();
              }}
            >
              <Form.Item name="completion_note" label="Complete — what happened?">
                <Input.TextArea rows={2} />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={completeFollowUp.isPending}>
                Mark done
              </Button>
            </Form>
          </Space>
        )}
      </Drawer>
    </div>
  );
};

export default SalesCalendarPage;
